/* eslint-disable max-lines */
/* eslint-disable consistent-return */
import React, { useRef, useState, useEffect, useMemo } from 'react';
import { Form, Card, Typography, Modal } from 'antd';
import { useSimpleList, useTable } from '@refinedev/antd';
import apiEndpoint from 'core/common/apiEndPoints';
import {
  Chart,
  BarElement,
  BarController,
  CategoryScale,
  LinearScale,
  Tooltip,
  Legend,
  LineElement,
  LineController,
  PointElement,
} from 'chart.js';

import { Chart as ReactChart } from 'react-chartjs-2';

import { ReactGrid } from '@silevis/reactgrid';
import '@silevis/reactgrid/styles.css';
import AttributeReviewForm from 'pages/attributeReview/AttributeReviewForm';
import AttributeReviewData from 'pages/attributeReview/interface';
import { useTranslate } from '@refinedev/core';
import {
  leftPalette,
  rightPalette,
  labelColWidth,
  rightAxisWidth,
  colWidth,
  chartHeight,
  tagBaseStyle,
  buildAssignedDatasets,
  buildScales,
} from 'pages/attributeReview/AttributeReviewConfig';
import {
  buildQueryString,
  buildTableData,
} from 'pages/attributeReview/AttributeReviewUtils';
import InsightView from 'pages/overview/insightView';
import AcademicCapSvg from 'assets/images/team performance/academicCapSvg';
import getFormattedDateByFrequency from 'core/utils/getFormattedDateByFrequency';

// Register Chart.js components
Chart.register(
  BarController,
  LineController,
  BarElement,
  LineElement,
  PointElement,
  CategoryScale,
  LinearScale,
  Tooltip,
  Legend
);

const { Title } = Typography;

const AttributeReview = () => {
  const [form] = Form.useForm();
  const [filters, setFilters] = useState({});
  const [selectedRulePlanId, setSelectedRulePlanId] = useState<string>('');
  const [showInsightModal, setShowInsightModal] = useState(false);
  const [blinkBtn, setBlinkBtn] = useState(false);
  const [chartKey, setChartKey] = useState(0); // ← Force re-render key
  const graphsref = useRef<HTMLDivElement>(null);
  const [selectedProgramConfigId, setSelectedProgramConfigId] =
    useState<string>('');
  const t = useTranslate();

  const { listProps: workflowListProps } = useSimpleList({
    resource: `${apiEndpoint.programWorkflowDetails}`,
    dataProviderName: 'programDataProvider',
    pagination: { mode: 'off' },
  });

  const workflowSource = workflowListProps?.dataSource as unknown as {
    status: string;
    results: {
      workflow_unique_id: string;
      workflow_name: string;
      program_config_id: string;
      program_config_name: string | null;
      frequency_id: string;
    }[];
    message: string;
    status_code: number;
  };

  const workflowList = workflowSource?.results || [];

  useEffect(() => {
    if (workflowList.length === 1 && !selectedProgramConfigId) {
      setSelectedProgramConfigId(workflowList[0].program_config_id);
    }
  }, [workflowList]);

  const showWorkflowDropdown = workflowList.length > 1;

  const { tableProps: rulePlanProps } = useTable({
    resource: `${apiEndpoint.getHistorialRulePlanIds}${
      selectedProgramConfigId
        ? `/?program_config_id=${selectedProgramConfigId}`
        : ''
    }`,
    dataProviderName: 'payoutProvider',
    pagination: { mode: 'off' },
    syncWithLocation: false,
    queryOptions: {
      enabled: showWorkflowDropdown ? !!selectedProgramConfigId : true,
    },
  });

  const rulePlanSource = rulePlanProps?.dataSource as unknown as {
    results?: { rule_plan_id: string; rule_plan_name: string }[];
  };
  const rulePlanList = rulePlanSource?.results ?? [];

  useEffect(() => {
    if (rulePlanList.length === 1 && !selectedRulePlanId) {
      setSelectedRulePlanId(rulePlanList[0].rule_plan_id);
    }
  }, [rulePlanList]);

  const rulePlanQuery = selectedRulePlanId
    ? `rule_plan_id=${selectedRulePlanId}`
    : '';
  const programConfigQuery =
    showWorkflowDropdown && selectedProgramConfigId
      ? `program_config_id=${selectedProgramConfigId}`
      : '';
  const filtersQuery = buildQueryString(filters);

  const queryString = [filtersQuery, rulePlanQuery, programConfigQuery]
    .filter(Boolean)
    .join('&');

  const { tableProps: dataListProps } = useTable({
    resource: `${apiEndpoint.getAttributeWiseData}/?${queryString}`,
    dataProviderName: 'payoutProvider',
    pagination: { mode: 'server' },
    syncWithLocation: false,
    queryOptions: {
      enabled: !!filtersQuery && !!selectedRulePlanId,
    },
  });

  // Force chart to remount when data changes
  // eslint-disable-line consistent-return
  useEffect(() => {
    if (dataListProps?.dataSource) {
      // Small delay to ensure data is fully loaded
      const timer = setTimeout(() => {
        setChartKey((prev) => prev + 1);
      }, 50);
      return () => clearTimeout(timer);
    }
  }, [dataListProps?.dataSource]);

  const dataList =
    (dataListProps?.dataSource as unknown as AttributeReviewData) || {
      chartData: [],
      differentiatorData: [],
      insightData: [],
    };

  const rawnsights = (dataList as any)?.insights ?? null;
  const insightData = rawnsights
    ? {
        ...rawnsights,
        plans:
          rawnsights.plans?.filter(
            (p: any) => p.planId === selectedRulePlanId
          ) ?? [],
      }
    : null;

  useEffect(() => {
    if (insightData) {
      setBlinkBtn(true);
      const time = setTimeout(() => setBlinkBtn(false), 5600);
      return () => clearTimeout(time);
    }
    return undefined;
  }, [insightData, selectedRulePlanId]);

  const assignedDatasets = useMemo(
    () => buildAssignedDatasets(dataList.chartData ?? []),
    [dataList.chartData]
  );

  const hasSecondAxis = assignedDatasets.some((ds) => ds.yAxisID === 'y1');
  const leftDatasets = assignedDatasets.filter((ds) => ds.yAxisID === 'y');
  const rightDatasets = assignedDatasets.filter((ds) => ds.yAxisID === 'y1');
  const leftAxisLabel = leftDatasets
    .map((ds) => ds.label)
    .filter(Boolean)
    .join(' / ');
  const rightAxisLabel = rightDatasets
    .map((ds) => ds.label)
    .filter(Boolean)
    .join(' / ');
  const leftTagColor = leftDatasets[0]?.solidColor ?? leftPalette[0].solid;
  const rightTagColor = rightDatasets[0]?.solidColor ?? rightPalette[0].solid;

  const numTimelines = dataList.differentiatorData.length;
  const numDatasets = assignedDatasets.length || 1;
  const rightAxisPx = hasSecondAxis ? rightAxisWidth : 0;
  const innerWidth =
    labelColWidth + numTimelines * numDatasets * colWidth + rightAxisPx;

  const chartData = useMemo(
    () => ({
      labels: dataList.differentiatorData.map(
        (item) =>
          getFormattedDateByFrequency(item.frequency_type, item.timeline)
            ?.label ?? item.timeline
      ),
      datasets: assignedDatasets,
    }),
    [assignedDatasets, dataList.differentiatorData]
  );

  const chartOptions: any = useMemo(
    () => ({
      responsive: true,
      maintainAspectRatio: false,
      animation: { duration: 300, easing: 'easeOutCubic' },
      scales: buildScales(
        leftAxisLabel,
        rightAxisLabel,
        leftTagColor,
        rightTagColor,
        hasSecondAxis
      ),
      plugins: {
        legend: { display: false },
        tooltip: {
          mode: 'index' as const,
          intersect: false,
          callbacks: {
            title: (items: any) => {
              const idx = items[0]?.dataIndex;
              const item = dataList.differentiatorData[idx];
              return (
                getFormattedDateByFrequency(
                  item?.frequency_type,
                  item?.timeline
                )?.label ?? ''
              );
            },
          },
        },
      },
    }),
    [
      leftAxisLabel,
      rightAxisLabel,
      leftTagColor,
      rightTagColor,
      hasSecondAxis,
      dataList.differentiatorData,
    ]
  );

  const hasFiltersSelected = Object.keys(filters).length > 0;
  const hasChart = hasFiltersSelected && chartData.datasets.length > 0;
  const hasTable = hasFiltersSelected && dataList.differentiatorData.length > 0;
  const hasInsight = hasFiltersSelected && !!insightData?.plans?.length;

  const { tableColumns, metricsHeader, tableRows } = buildTableData(
    dataList,
    hasSecondAxis,
    labelColWidth,
    colWidth,
    rightAxisWidth
  );

  const showRulePlanDropdown = showWorkflowDropdown
    ? !!selectedProgramConfigId && rulePlanList.length > 1
    : rulePlanList.length > 1;
  // Handle apply button click - reset chart and refetch
  const handleApplyFilters = async (newFilters: any) => {
    setFilters(newFilters);
    setChartKey((prev) => prev + 1); // Force chart remount
    // Small delay to ensure state updates
    setTimeout(() => {}, 100);
  };

  return (
    <Card
      bodyStyle={{
        padding: 'var(--spacing-lg) var(--spacing-lg) var(--spacing-xl)',
      }}
    >
      <Title
        level={4}
        style={{
          marginBottom: 0,
          color: 'var(--neutral-900)',
          fontWeight: 700,
        }}
      >
        {t('pages.historicalPayout.title')}
      </Title>

      <Card
        title={t('pages.historicalPayout.filterAttributes')}
        bordered
        style={{
          marginTop: 'var(--spacing-md)',
          borderRadius: 8,
          background: 'var(--neutral-20)',
        }}
      >
        <AttributeReviewForm
          formData={{ form }}
          stateFunctionHandler={{ setFilters: handleApplyFilters }}
          rulePlanId={selectedRulePlanId}
          programConfigId={selectedProgramConfigId} // ← add this
          rulePlanList={showRulePlanDropdown ? rulePlanList : []}
          workflowList={showWorkflowDropdown ? workflowList : []}
          onWorkflowChange={(val: string) => {
            setSelectedProgramConfigId(val ?? '');
            setSelectedRulePlanId('');
            setFilters({});
            form.resetFields();
          }}
          onRulePlanChange={(val: string) => {
            setSelectedRulePlanId(val ?? '');
            setFilters({});
            setChartKey((prev) => prev + 1);
            form.resetFields([
              'dateRange',
              'differentiator',
              'compareKey',
              'fieldFilters',
            ]);
          }}
        />
      </Card>

      <div
        ref={graphsref}
        style={{
          overflowX: 'auto',
          width: '100%',
          marginTop: 'var(--spacing-lg)',
        }}
      >
        {hasInsight ? (
          <div
            style={{
              display: 'flex',
              justifyContent: 'flex-end',
              marginBottom: 'var(--spacing-sm)',
            }}
          >
            <div
              onClick={() => setShowInsightModal(true)}
              className={`btn-width ${blinkBtn ? 'insight-blink w-auto' : 'w-auto'} flex items-center justify-center border border-primary]`}
            >
              <span className="text-primary py-sm px-sm rounded flex items-center gap-sm">
                <AcademicCapSvg /> Insights
              </span>
            </div>
          </div>
        ) : null}

        <div style={{ width: innerWidth, minWidth: innerWidth }}>
          {hasChart && Boolean(leftAxisLabel || rightAxisLabel) ? (
            <div
              style={{
                display: 'flex',
                gap: 'var(--spacing-sm)',
                marginBottom: 'var(--spacing-sm)',
              }}
            >
              {leftAxisLabel ? (
                <span
                  style={{
                    ...tagBaseStyle,
                    background: `${leftTagColor}14`,
                    color: leftTagColor,
                    border: `1px solid ${leftTagColor}30`,
                  }}
                >
                  <span
                    style={{
                      width: 7,
                      height: 7,
                      borderRadius: 2,
                      background: leftTagColor,
                      display: 'inline-block',
                      flexShrink: 0,
                    }}
                  />
                  {leftAxisLabel} (Bar)
                </span>
              ) : null}
              {hasSecondAxis && Boolean(rightAxisLabel) ? (
                <span
                  style={{
                    ...tagBaseStyle,
                    background: `${rightTagColor}14`,
                    color: rightTagColor,
                    border: `1px solid ${rightTagColor}30`,
                  }}
                >
                  <span
                    style={{
                      width: 7,
                      height: 7,
                      borderRadius: 2,
                      background: rightTagColor,
                      display: 'inline-block',
                      flexShrink: 0,
                    }}
                  />
                  {rightAxisLabel} (Line)
                </span>
              ) : null}
            </div>
          ) : null}

          {hasChart ? (
            <div
              key={chartKey} // ← Force React to remount the chart component
              style={{
                width: innerWidth,
                height: chartHeight,
                background: 'var(--neutral-10)',
                border: '1px solid var(--neutral-50)',
                borderRadius: '8px 8px 0 0',
                boxSizing: 'border-box',
                padding: 'var(--spacing-md) 0 var(--spacing-sm)',
                boxShadow: '0 1px 4px rgba(0,0,0,0.06)',
              }}
            >
              <ReactChart type="bar" data={chartData} options={chartOptions} />
            </div>
          ) : null}

          {hasTable ? (
            <div
              style={{
                width: innerWidth,
                overflowX: 'hidden',
                overflowY: 'auto',
                maxHeight: tableRows.length > 10 ? 400 : 'none',
                border: '1px solid var(--neutral-50)',
                borderTop: hasChart ? 'none' : '1px solid var(--neutral-50)',
                borderRadius: hasChart ? '0 0 8px 8px' : '8px',
                boxShadow: '0 1px 4px rgba(0,0,0,0.04)',
              }}
            >
              <ReactGrid
                enableFullWidthHeader={false}
                columns={tableColumns}
                rows={[metricsHeader, ...tableRows]}
                enableRangeSelection={false}
                enableFillHandle={false}
                stickyTopRows={1}
              />
            </div>
          ) : null}
        </div>
      </div>

      <Modal
        open={showInsightModal}
        onCancel={() => setShowInsightModal(false)}
        width={640}
        footer={null}
      >
        <InsightView insightData={insightData} />
      </Modal>
    </Card>
  );
};

export default AttributeReview;
